import React, { useState, useEffect, useCallback, useRef } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { FaPaperPlane, FaSyncAlt, FaListUl, FaVial, FaRedo, FaTimes, FaExclamationTriangle, FaBuilding, FaIdCard, FaClipboardCheck, FaLink, FaSitemap, FaPen } from 'react-icons/fa';
import TabelaLoading from '../../components/TabelaLoading';
import api from '../../services/api';
import Alert from '../../components/Alert';
import { FinField, FinInput } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import DisparoRedes from './DisparoRedes';
import '../../css/financeiro-parcela-pagar.css';

const INTERVALO_POLLING = 2000;

// Sem NENHUM byte do stream nesse prazo, assume-se que o proxy bufferizou a
// resposta e cai-se para o polling. É curto de propósito: o servidor emite um
// keepalive a cada 500ms, então 8s sem nada só acontece se o transporte falhou.
const TIMEOUT_SSE = 8000;

// `sem_email` é o filtro que a área pediu — a lista nominal de quem ainda não
// tem contato cadastrado.
const FILTROS_ENVIO = [
    { valor: '', rotulo: 'Todos' },
    { valor: 'sucesso', rotulo: 'Enviados' },
    // "Não entregues" e não "Não enviados": o Painel de Fornecedores já tem um chip
    // com esse nome e outra definição: lá, `falha` com token vivo conta como enviado,
    // porque o SES pode ter entregue antes de a rotina estourar. Dois chips homônimos
    // com números diferentes na mesma sessão é o defeito que o StatusFormulario existe
    // para não deixar acontecer. Aqui a pergunta é sobre a ÚLTIMA tentativa.
    { valor: 'nao_enviado', rotulo: 'Não entregues' },
    { valor: 'sem_email', rotulo: 'Sem e-mail' },
    { valor: 'falha', rotulo: 'Falhas' },
    { valor: 'simulado', rotulo: 'Simulados' },
    { valor: 'amostra', rotulo: 'Amostras' },
];

const ROTULO_STATUS_ENVIO = {
    sucesso: 'Enviado',
    simulado: 'Simulado',
    amostra: 'Amostra',
    sem_email: 'Sem e-mail',
    falha: 'Falha',
    gerado: 'Link gerado',
};

/**
 * `dd/mm/aaaa hh:mm` a partir do que o banco devolve cru (`aaaa-mm-dd hh:mm:ss`)
 * ou de um ISO8601. Com a listagem agrupada por fornecedor, a hora importa: um
 * lote inteiro cai no mesmo dia, e sem ela duas linhas ficam iguais.
 */
const formatarDataHora = (valor) => {
    if (!valor) return '—';

    const [data, hora] = String(valor).replace('T', ' ').split(' ');
    const [ano, mes, dia] = String(data).split('-');

    if (!ano || !mes || !dia) return String(valor);

    return `${dia}/${mes}/${ano}${hora ? ` ${hora.slice(0, 5)}` : ''}`;
};

const faixaDoEnvio = (envio) => {
    if (envio.status === 'falha' && envio.motivo === 'sem_email') return 'sem_email';
    // A amostra é `simulado` no banco, mas é a única linha simulada em que saiu
    // e-mail de verdade — precisa se distinguir das outras na tela.
    if (envio.status === 'simulado' && envio.motivo === 'amostra') return 'amostra';
    return envio.status;
};

/**
 * Categorias de lote — espelham `FornecedorDisparo::ORIGEM_*` no back.
 *
 * `manual` é o lote que alguém disparou pela tela; `automatica` é o que a varredura
 * de lembretes criou (`POST fornecedor/lembretes`), cobrando quem recebeu o convite
 * e não respondeu nos marcos de 3 e 7 dias.
 */
const ORIGEM_MANUAL = 'manual';
const ORIGEM_AUTOMATICA = 'automatica';
// Não é origem de lote: link gerado na aba Formulário do cadastro não tem lote. Viaja no
// mesmo parâmetro `origem` — ver FornecedorDisparoController::ABA_GERADOS.
const ABA_GERADOS = 'gerado';
// Também não é origem de lote: é outra forma de ver os fornecedores, agrupados pela rede.
// Não consulta envios — ver DisparoRedes.
const ABA_REDES = 'redes';

const ABAS = [
    {
        valor: ORIGEM_MANUAL,
        rotulo: 'Disparos',
        descricao: 'Envios feitos por alguém a partir desta tela.',
    },
    {
        valor: ABA_REDES,
        rotulo: 'Redes',
        descricao: 'Fornecedores agrupados pela rede (grupo de fornecedor), com a sede primeiro. O link vai para a '
            + 'sede, e quem o abre escolhe a filial da rede antes de preencher. A filial preenchida pelo link da '
            + 'sede ganha o badge "pela rede".',
    },
    {
        valor: ORIGEM_AUTOMATICA,
        rotulo: 'Lembretes automáticos',
        descricao: 'Cobranças que o sistema envia sozinho a quem recebeu o formulário e não respondeu. '
            + 'Não há o que disparar aqui — esta aba é o registro do que já foi enviado.',
    },
    {
        valor: ABA_GERADOS,
        rotulo: 'Links gerados',
        descricao: 'Links gerados na aba Formulário do cadastro do fornecedor e entregues por fora do sistema '
            + '(WhatsApp, e-mail próprio). Nenhum e-mail saiu do sistema para eles. Contam como enviados: o '
            + 'fornecedor passa a dever o formulário, e o disparo pergunta antes de mandar o e-mail para eles.',
    },
];

const PILL_STATUS = {
    sucesso: 'is-ok',
    simulado: 'is-info',
    amostra: 'is-warn',
    sem_email: 'is-warn',
    falha: 'is-danger',
    gerado: 'is-info',
};

/**
 * Disparo em lote do formulário de cadastro de fornecedor.
 *
 * O POST é disparado SEM await de propósito: ele fica preso no servidor durante
 * todo o processamento (não há worker de fila, e no Cloud Run a CPU só é
 * garantida enquanto há request em voo). O acompanhamento lê os contadores que o
 * POST grava no banco a cada item.
 *
 * ── Dois transportes para o mesmo dado ────────────────────────────────────
 *
 * SSE primeiro: uma conexão aberta em vez de N requisições curtas. Cada poll do
 * cliente custa um boot completo do framework para ler UMA linha — num lote longo
 * viram centenas de boots. No stream o boot acontece uma vez.
 *
 * Polling como fallback automático: se o proxy do Cloud Run bufferizar a resposta,
 * o SSE não entrega nada e falha CALADO — sem erro, sem log, só uma tela parada.
 * O watchdog abaixo detecta isso e troca de transporte sozinho, então a tela
 * funciona nos dois mundos e o transporte ativo fica visível no cabeçalho.
 */
export default function PainelDisparoFornecedor() {
    const token = localStorage.getItem('token');
    const location = useLocation();
    const history = useHistory();
    const { state: navState } = location;
    // Categoria de lote em exibição — é a aba, e ela recorta TUDO abaixo dela: os
    // chips, o seletor de lote e a listagem.
    //
    // São dois assuntos, não duas visões do mesmo. O disparo manual é uma ação com
    // botão, confirmação e acompanhamento ao vivo; o lembrete automático é o
    // registro do que a varredura fez enquanto ninguém olhava, e não há o que
    // apertar nele. Misturados na mesma lista, "quem eu cobrei" e "quem o sistema
    // cobrou" viram a mesma coluna, e a pergunta que a área faz é sempre uma das
    // duas — nunca a soma.
    const [aba, setAba] = useState(ORIGEM_MANUAL);
    // O Atualizar do topo também recarrega as redes quando é a aba aberta.
    const [recargaRedes, setRecargaRedes] = useState(0);

    const [disparo, setDisparo] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [transporte, setTransporte] = useState(null);

    const [envios, setEnvios] = useState([]);
    const [totalEnvios, setTotalEnvios] = useState(0);
    const [contadores, setContadores] = useState({});
    const [filtroStatus, setFiltroStatus] = useState('');
    const [busca, setBusca] = useState('');
    const [buscaAplicada, setBuscaAplicada] = useState('');
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(25);
    const [carregandoEnvios, setCarregandoEnvios] = useState(false);
    const [desdeFiltro, setDesdeFiltro] = useState('');
    const [ateFiltro, setAteFiltro] = useState('');
    // Lotes do tenant + o lote escolhido. O seletor existe porque o filtro de
    // período é granularidade de DIA: dois disparos no mesmo dia eram
    // impossíveis de separar, e "o disparo corrente" era o único outro recorte.
    const [disparos, setDisparos] = useState([]);
    // Lista, não id único: o filtro soma destinatários de vários lotes. Vazia é o
    // padrão e significa TODOS OS DISPAROS — é o que o placeholder diz, e é o que o
    // servidor faz. Antes vazio queria dizer "o lote corrente", e o seletor exibia
    // "Disparo atual" enquanto a lista trazia outro recorte.
    const [disparoFiltro, setDisparoFiltro] = useState([]);

    // Armado no clique de disparo/reenvio e consumido quando o lote encerra: é ele
    // que diz que o lote a aparecer na lista foi PEDIDO por você, e não disparado por
    // um colega — só o seu move o seletor. Ref e não estado porque não pinta nada:
    // como estado, só somaria um render no meio do disparo.
    const seguirLoteNovo = useRef(false);

    // null = fechado. Envio real nunca acontece sem passar por aqui.
    const [confirmacao, setConfirmacao] = useState(null);

    // Linha da lista escolhida — abre o drawer de destinos. Guarda o envio inteiro,
    // não só o id: o cabeçalho do drawer mostra de quem é a linha, e reabrir a lista
    // para redescobrir isso não valeria a chamada.
    const [linhaEscolhida, setLinhaEscolhida] = useState(null);

    // Varredura de lembretes acionada pela tela — a MESMA rotina que o agendador chama, pela rota
    // `fornecedor/lembretes/manual` (perfil, dentro do auth) em vez da rota de máquina (token).
    //
    // `varreduraArmada` é a confirmação do envio real, em dois cliques em vez de modal: o botão
    // troca de rótulo e só a segunda batida manda e-mail. A simulação não arma nada — ela não sai
    // da máquina, então pedir confirmação para ela só ensinaria a clicar sem ler.
    const [varredura, setVarredura] = useState(null);
    const [varrendo, setVarrendo] = useState(false);
    const [varreduraArmada, setVarreduraArmada] = useState(false);

    const rodarVarredura = (simulacao) => {
        setVarrendo(true);
        setVarredura(null);
        setVarreduraArmada(false);

        api({
            method: 'post',
            action: '/fornecedor/lembretes/manual',
            token,
            data: { simulacao },
        })
            .then(resposta => setVarredura(resposta.data?.data ?? null))
            .catch(erro => setVarredura({
                // Mensagem genérica quando o servidor não mandar uma própria: o operador não tem o
                // que fazer com código de erro, e a rota devolve 403 com texto pronto.
                erro: erro?.response?.data?.error
                    || 'Não foi possível enviar os lembretes agora. Tente de novo em instantes.',
            }))
            .finally(() => {
                setVarrendo(false);
                // O envio real muda o que a lista mostra; a simulação também grava linha
                // `simulado`, então nos dois casos vale recarregar.
                buscarEnvios();
            });
    };

    const emAndamento = disparo?.status === 'em_andamento';

    const buscar = useCallback(() => {
        return api({ method: 'get', action: '/fornecedor/disparo', token })
            .then(response => setDisparo(response.data?.data ?? null))
            .catch(() => { })
            .finally(() => setCarregando(false));
        // eslint-disable-next-line
    }, []);

    useEffect(() => { buscar(); }, [buscar]);

    // Seleção vinda do PainelFornecedor. A confirmação abre sozinha porque o
    // operador já tomou a decisão lá — reabrir a escolha aqui seria um passo a mais
    // sem informação nova. `cobranca` só muda o estado inicial do checkbox.
    useEffect(() => {
        if (Array.isArray(navState?.alvos) && navState.alvos.length > 0) {
            abrirConfirmacao(
                { alvos: navState.alvos },
                false,
                !!navState.cobranca || !!navState.revisao,
                !!navState.cobranca,
                !!navState.revisao,
            );

            // Consome o state do histórico. Ele vive no `window.history.state` e
            // SOBREVIVE a F5 e ao botão Avançar — sem limpar, um refresh meia hora
            // depois reabria o modal de disparo REAL com os mesmos alvos, a um Enter
            // distraído de um segundo envio.
            history.replace({ pathname: location.pathname, search: location.search });
        }
        // eslint-disable-next-line
    }, []);

    // Poll enquanto o POST está em voo OU o servidor diz que há disparo rolando.
    //
    // O `enviando` não é redundante: ele cobre a janela entre o clique e a linha do
    // disparo existir no banco. Sem ele, o início do polling dependeria de um único
    // GET logo após o clique — e quando esse GET perde a corrida e volta com o
    // disparo ANTERIOR (já concluído), nenhum intervalo é criado e a tela congela
    // até o fim, mesmo com o lote rodando normalmente no servidor.
    //
    // Como o POST só responde ao terminar o lote, `enviando` permanece true por
    // toda a duração — é o sinal confiável.
    const devePollar = emAndamento || enviando;

    useEffect(() => {
        if (!devePollar) {
            setTransporte(null);
            return undefined;
        }

        let cancelado = false;
        let intervalo = null;
        const controller = new AbortController();

        const cairParaPolling = (motivo) => {
            if (cancelado || intervalo) return;
            controller.abort();
            setTransporte('polling');
            console.warn('[disparo] SSE indisponível, usando polling —', motivo);
            buscar();
            intervalo = setInterval(buscar, INTERVALO_POLLING);
        };

        const watchdog = setTimeout(
            () => cairParaPolling(`sem nenhum byte em ${TIMEOUT_SSE / 1000}s`),
            TIMEOUT_SSE
        );

        (async () => {
            try {
                // fetch + ReadableStream em vez de EventSource: EventSource não
                // envia header Authorization, e mandar o token na query o jogaria
                // no access log do Apache (que vai para o Cloud Logging).
                const resposta = await fetch(
                    `${process.env.REACT_APP_API_URL}/fornecedor/disparo/stream`,
                    {
                        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/event-stream' },
                        signal: controller.signal,
                    }
                );

                if (!resposta.ok || !resposta.body) {
                    throw new Error(`HTTP ${resposta.status}`);
                }

                const leitor = resposta.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                let encerrouSozinho = false;
                let fimAnunciado = false;

                while (!cancelado) {
                    const { done, value } = await leitor.read();
                    if (done) {
                        encerrouSozinho = true;
                        break;
                    }

                    // Qualquer byte já prova que o proxy não bufferizou — inclusive
                    // o comentário de keepalive, que não vira evento.
                    clearTimeout(watchdog);
                    setTransporte('sse');

                    buffer += decoder.decode(value, { stream: true });

                    let corte;
                    while ((corte = buffer.indexOf('\n\n')) !== -1) {
                        const bruto = buffer.slice(0, corte);
                        buffer = buffer.slice(corte + 2);

                        // `event: fim` é o servidor dizendo que o lote acabou — o
                        // encerramento previsto, não uma queda. Sem reconhecê-lo, o
                        // fechamento logo em seguida caía no ramo de falha, e a tela
                        // exibia "polling" no último segundo de todo disparo, como se
                        // o SSE tivesse quebrado depois de fazer o trabalho inteiro.
                        if (bruto.split('\n').some(l => l.trim() === 'event: fim')) {
                            fimAnunciado = true;
                        }

                        const linha = bruto.split('\n').find(l => l.startsWith('data:'));
                        if (!linha) continue; // comentário keepalive

                        try {
                            setDisparo(JSON.parse(linha.slice(5).trim()));
                            setCarregando(false);
                        } catch (e) {
                            // payload truncado ou inválido — ignora este evento
                        }
                    }
                }

                // O stream fechou por conta própria. Se o lote ainda está rolando,
                // ninguém mais atualizaria a tela — ela congelaria com o número do
                // último evento recebido. Assumir com polling é o comportamento
                // correto, e cobre inclusive o stream que encerra cedo por engano.
                if (encerrouSozinho && !cancelado && !fimAnunciado) {
                    cairParaPolling('stream encerrado pelo servidor');
                }
            } catch (e) {
                if (!cancelado && e.name !== 'AbortError') {
                    cairParaPolling(e.message);
                }
            }
        })();

        return () => {
            cancelado = true;
            clearTimeout(watchdog);
            controller.abort();
            if (intervalo) clearInterval(intervalo);
        };
    }, [devePollar, buscar, token]);

    /**
     * Abre a confirmação com a contagem REAL da seleção.
     *
     * Envio é irreversível — e-mail entregue não volta. O modal nunca aparece com
     * número estimado: consulta a prévia no servidor e só então mostra quantos
     * seriam atingidos e quantos ficaram de fora por já terem recebido.
     */
    // Sequência das prévias. Duas chamadas concorrentes (marcar/desmarcar "reenviar"
    // rápido) não têm ordem garantida: sem isto, a resposta ANTIGA podia chegar por
    // último e o modal exibia uma contagem enquanto o POST mandava outra — num envio
    // que não se desfaz. Vence a última PEDIDA, não a última a chegar.
    const seqPrevia = useRef(0);

    // Recebe o ESTADO inteiro, não os campos soltos. Cada flag que decide o recorte
    // precisa chegar aqui exatamente como chega no POST — se uma ficar de fora, o
    // modal confirma "Enviar para 15" e o envio sai para outro conjunto, num e-mail
    // que não se desfaz. Com quatro booleanos posicionais isso era questão de tempo:
    // bastava um chamador novo esquecer o último argumento.
    const carregarPrevia = (estado) => {
        const seq = ++seqPrevia.current;

        api({
            // POST porque a seleção viaja no corpo: com ~260 ids a query estourava o
            // limite de linha do Apache e voltava 414, travando o modal.
            method: 'post',
            action: '/fornecedor/disparo/previa',
            token,
            data: {
                ...estado.selecao,
                incluirJaEnviados: !!estado.incluirJaEnviados,
                incluirGerados: !!estado.incluirGerados,
                cobranca: !!estado.cobranca,
                incluirJaPreencheram: !!estado.incluirJaPreencheram,
                revisao: !!estado.revisao,
            },
        })
            .then(response => {
                if (seq !== seqPrevia.current) return;
                setConfirmacao(c => c && ({ ...c, carregando: false, previa: response.data?.data ?? null }));
            })
            .catch(() => {
                if (seq !== seqPrevia.current) return;
                setConfirmacao(c => c && ({ ...c, carregando: false, previa: null }));
            });
    };

    // `cobranca` é SEPARADO de `incluirJaEnviados`, e não um apelido dele. Cobrar
    // precisa dos dois ao mesmo tempo, por motivos opostos: alcançar de novo quem JÁ
    // RECEBEU (senão o dedupe zera o alvo) e não alcançar quem JÁ RESPONDEU. Enquanto
    // só o primeiro viajava, o backend não tinha como distinguir cobrança de disparo
    // novo, e a segunda regra não rodava neste caminho.
    const abrirConfirmacao = (selecao, simulacao, incluirJaEnviados = false, cobranca = false, revisao = false) => {
        // `incluirJaPreencheram` nasce sempre falso, inclusive quando o reenvio já
        // vem marcado: são perguntas diferentes. "Já recebeu" é quem se quer
        // alcançar de novo; "já preencheu" é quem não deve ser incomodado — pedir
        // de novo um cadastro que a pessoa acabou de mandar é o que o cliente
        // perguntou se acontecia. Na revisão ele nem entra em cena: lá o alvo É
        // quem preencheu, e o servidor descarta a flag.
        // A caixa "reenviar também para quem já recebeu" nasce SEMPRE desmarcada
        // onde é visível. Cobrança e revisão continuam com a flag ligada por dentro
        // — sem ela o dedupe zeraria o alvo —, e nesses dois a caixa não aparece.
        // `incluirGerados` nasce falso em todos os caminhos, cobrança inclusive: quem tem link
        // gerado já recebeu o link por fora, e mandar o e-mail também é decisão explícita.
        const estado = { selecao, simulacao, incluirJaEnviados, cobranca, revisao, incluirJaPreencheram: false, incluirGerados: false };
        setConfirmacao({ ...estado, emailsAmostra: '', carregando: true, previa: null });
        carregarPrevia(estado);
    };

    // Lista da amostra. Vazia é legítimo: sem endereço, "Simular" segue sendo a
    // contagem seca de sempre — que é o que responde "quem não tem e-mail".
    const amostraDigitada = (confirmacao?.emailsAmostra || '')
        .split(/[,;]+/)
        .map(e => e.trim())
        .filter(Boolean);

    // Mesma regra do servidor. Valida antes de habilitar o botão porque o envio
    // é irreversível: endereço torto só se descobre depois que a mensagem saiu.
    const erroAmostra = (() => {
        const invalido = amostraDigitada.find(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
        if (invalido) return `E-mail inválido: ${invalido}`;
        if (amostraDigitada.length > 5) return 'Máximo de 5 endereços.';
        return null;
    })();

    const alternarJaEnviados = (incluirJaEnviados) => {
        setConfirmacao(c => c && ({ ...c, incluirJaEnviados, carregando: true }));
        carregarPrevia({ ...confirmacao, incluirJaEnviados });
    };

    const alternarJaPreencheram = (incluirJaPreencheram) => {
        setConfirmacao(c => c && ({ ...c, incluirJaPreencheram, carregando: true }));
        carregarPrevia({ ...confirmacao, incluirJaPreencheram });
    };

    const alternarGerados = (incluirGerados) => {
        setConfirmacao(c => c && ({ ...c, incluirGerados, carregando: true }));
        carregarPrevia({ ...confirmacao, incluirGerados });
    };

    const confirmarDisparo = () => {
        const { selecao, simulacao, incluirJaEnviados, cobranca, incluirJaPreencheram, revisao, incluirGerados } = confirmacao || {};
        // `busca` PRECISA vir aqui. Mandá-la só para a prévia é pior que não mandar:
        // a prévia conta o recorte estreito, o POST chega com os quatro filtros
        // vazios e o backend cai no ramo "todos os fornecedores ativos". O modal
        // diria 3 e o disparo atingiria a base inteira.
        // `disparo` na mesma lista e pelo mesmo motivo do `busca`: a prévia espalha
        // a seleção inteira (`...selecao`), mas o POST destrincha campo a campo — o
        // que ficar de fora aqui chega vazio no backend e alarga o alvo em silêncio.
        // `origem` na mesma lista, e pelo mesmo motivo: a listagem é recortada pela
        // aba, e o servidor resolve o "último envio" de cada fornecedor DENTRO do
        // recorte que recebe. Sem ela aqui, o alvo do reenvio seria calculado sobre
        // todas as categorias — um fornecedor cujo último contato foi um lembrete
        // automático mudaria de faixa e entraria (ou sairia) do alvo sem que a lista
        // na tela tivesse mudado.
        const { recorte, statusEnvio, disparo: disparoSelecao, desde, ate, alvos, busca, origem } = selecao || {};
        const emailsSimulacao = simulacao !== false ? amostraDigitada : [];
        setConfirmacao(null);
        setEnviando(true);
        // O envio para as sedes vira um lote manual como qualquer outro, e o acompanhamento
        // dele mora na aba Disparos.
        if (aba === ABA_REDES) setAba(ORIGEM_MANUAL);
        // Disparo e reenvio ARMAM a seleção do lote novo — o clique foi seu, e olhar
        // o resultado dele é o que se espera. Só arma: o id não existe neste instante,
        // e quem marca é o efeito lá embaixo, quando o lote encerra e entra na lista.
        seguirLoteNovo.current = true;

        // Sem await: a resposta deste POST só chega quando o lote inteiro termina.
        // keepalive mantém a request viva se a aba for fechada no meio.
        fetch(`${process.env.REACT_APP_API_URL}/fornecedor/disparo`, {
            method: 'POST',
            keepalive: true,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                simulacao: simulacao !== false,
                incluirJaEnviados: !!incluirJaEnviados,
                incluirGerados: !!incluirGerados,
                cobranca: !!cobranca,
                incluirJaPreencheram: !!incluirJaPreencheram,
                revisao: !!revisao,
                ...(recorte ? { recorte: true } : {}),
                ...(statusEnvio ? { statusEnvio } : {}),
                // `length`, não a lista pelada: `[]` é truthy em JS, e mandar um
                // `disparo` vazio faria o backend cair no ramo do lote corrente sem
                // que a tela tivesse pedido isso.
                ...(disparoSelecao && disparoSelecao.length > 0 ? { disparo: disparoSelecao } : {}),
                ...(desde ? { desde } : {}),
                ...(ate ? { ate } : {}),
                ...(alvos ? { alvos } : {}),
                ...(busca ? { busca } : {}),
                ...(origem ? { origem } : {}),
                ...(emailsSimulacao.length > 0 ? { emailsSimulacao } : {}),
            }),
        })
            .then(async (resposta) => {
                // QUALQUER resposta de erro, não só 409 e 422. O gate de perfil
                // responde 403 e o token expirado 401 — listar status a mão deixava
                // esses dois sumirem, e a tela ficava idêntica a antes do clique.
                // O 200 só volta no fim do lote.
                if (!resposta.ok) {
                    const corpo = await resposta.json().catch(() => ({}));
                    Alert({ icon: 'error', title: corpo.error || 'Não foi possível disparar' });
                }
            })
            .catch(() => {
                Alert({
                    icon: 'error',
                    title: 'Falha de conexão ao disparar',
                    html: 'Confira na tabela de envios se o lote chegou a começar antes de tentar de novo.',
                });
            })
            .finally(() => setEnviando(false));

        // Dá tempo de a linha do disparo existir antes da primeira leitura
        setTimeout(buscar, 500);
    };

    // A listagem só carrega com o lote encerrado. Durante o disparo os números
    // mudam a cada item, e recarregar 3 mil linhas a cada tick não ajudaria
    // ninguém — o acompanhamento ao vivo é a barra, a lista é o resultado.
    const disparoId = disparo?.id ?? null;

    // Encerrado o lote, o seletor vai sozinho para ELE — pelo id concreto, o mesmo
    // que a lista de lotes oferece. É a única forma de o filtro seguir um disparo:
    // não existe mais valor "atual", nem no seletor nem no servidor, então o campo
    // nunca nomeia algo que só a consulta resolve, e nunca diz uma coisa enquanto a
    // lista traz outra.
    useEffect(() => {
        // `enviando` no guarda, e não só `emAndamento`: entre o clique e a linha do
        // disparo existir, `emAndamento` ainda é falso e `disparoId` ainda é o lote
        // ANTERIOR. Sem isto o efeito rodaria no próprio commit do clique e marcaria
        // o lote velho — a listagem voltava filtrada no #57 logo depois de disparar o
        // #58, e o "Reenviar este recorte" a partir dali miraria o conjunto errado.
        if (emAndamento || enviando || !disparoId) return;
        if (!seguirLoteNovo.current) return;

        // Espera o lote entrar nas OPÇÕES, que chegam no mesmo GET dos envios. O
        // valor do seletor sai das próprias opções: marcar antes deixaria o campo em
        // branco com a listagem já filtrada — o campo dizendo uma coisa e a lista
        // trazendo outra, que é justamente o que esta tela não pode fazer.
        if (!disparos.some(lote => String(lote.id) === String(disparoId))) return;

        seguirLoteNovo.current = false;
        setDisparoFiltro([String(disparoId)]);
    }, [emAndamento, enviando, disparoId, disparos]);

    // Sem opção "todos": ela É o campo vazio, que o placeholder anuncia. Uma opção
    // e um vazio significando a mesma coisa dariam dois caminhos para o mesmo
    // recorte, e um deles teria que ser traduzido no servidor.
    //
    // E sem opção "atual": ela só faria sentido com um lote rodando, e é exatamente
    // aí que este campo não existe — o card dos Destinatários inteiro é renderizado
    // sob `!emAndamento`, então ela nunca chegaria a ser clicável. Acompanhar o
    // disparo que você acabou de fazer é trabalho do efeito acima, que marca o id
    // de verdade.
    const opcoesDisparo = [
        ...disparos.map(lote => ({
            value: String(lote.id),
            label: `#${lote.id} · ${formatarDataHora(lote.created_at)} · ${lote.total} alvo(s)${lote.simulacao ? ' · simulação' : ''}`,
        })),
    ];

    // Limpar devolve o campo ao vazio, que é o default: todos os disparos.
    const alterarDisparoFiltro = (selecionados) => {
        setDisparoFiltro((selecionados || []).map(o => o.value));
    };

    const controllerRef = useRef(null);

    const buscarEnvios = useCallback(() => {
        // Link gerado não depende de disparo: sem nenhum lote, a aba dele ainda consulta.
        if ((!disparoId && aba !== ABA_GERADOS) || aba === ABA_REDES) return;

        // Trocar filtro com a busca anterior em voo: a antiga é abortada e cai no
        // `catch` como ERR_CANCELED, que sai antes de tocar em estado. Vale desde o
        // axios 0.27, que lê `signal`.
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
        controllerRef.current = new AbortController();

        setCarregandoEnvios(true);
        api({
            signal: controllerRef.current.signal,
            method: 'get',
            action: '/fornecedor/disparo/envios',
            token,
            params: {
                status: filtroStatus,
                busca: buscaAplicada,
                disparo: disparoFiltro,
                desde: desdeFiltro,
                ate: ateFiltro,
                origem: aba,
                pagina,
                porPagina,
            },
        })
            .then(response => {
                const d = response.data?.data ?? {};
                setEnvios(d.envios ?? []);
                setTotalEnvios(d.total ?? 0);
                setContadores(d.contadores ?? {});
                setDisparos(d.disparos ?? []);
                setCarregandoEnvios(false);
            })
            .catch(error => {
                if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
                setCarregandoEnvios(false);
            });
        // eslint-disable-next-line
    }, [disparoId, filtroStatus, buscaAplicada, disparoFiltro, desdeFiltro, ateFiltro, aba, pagina, porPagina]);

    useEffect(() => () => {
        if (controllerRef.current) controllerRef.current.abort();
    }, []);

    useEffect(() => {
        if (emAndamento) return;
        buscarEnvios();
    }, [emAndamento, buscarEnvios]);

    // Trocar de filtro ou de busca com a página 3 aberta traria uma página vazia.
    useEffect(() => { setPagina(1); }, [filtroStatus, buscaAplicada, disparoFiltro, desdeFiltro, ateFiltro]);

    // Trocar de aba solta o lote marcado: ele é de OUTRA categoria, e o servidor
    // recorta pelas duas coisas. Mantido, o filtro fecharia a lista em zero e o
    // operador leria isso como "não há lembrete", quando o que há é um lote manual
    // marcado numa aba que só mostra automáticos.
    // Na aba de links gerados os chips de status somem; uma faixa marcada em outra aba
    // continuaria filtrando por baixo e zeraria a lista (e o alvo do reenvio).
    useEffect(() => {
        setDisparoFiltro([]);
        setPagina(1);
        if (aba === ABA_GERADOS) setFiltroStatus('');
    }, [aba]);

    const totalPaginas = Math.max(1, Math.ceil(totalEnvios / porPagina));

    // O chip "Todos" mostra a soma das faixas, não o `totalEnvios` — este último
    // já vem filtrado, e mostraria o mesmo número do chip selecionado.
    const totalGeralContadores = Object.values(contadores).reduce((soma, n) => soma + Number(n || 0), 0);

    // "Não enviados" é o complemento de "Enviados", calculado aqui em vez de virar
    // mais uma chave em `contadores`: lá as faixas particionam e são somadas para o
    // chip "Todos" — uma união entre elas faria a soma passar do total.
    const naoEnviados = totalGeralContadores - Number(contadores.sucesso || 0);

    const cards = [
        { titulo: 'Total', valor: disparo?.total ?? 0, cor: 'var(--fin-tx)' },
        { titulo: 'Sucesso', valor: disparo?.sucesso ?? 0, cor: 'var(--fin-ok)' },
        // Sem e-mail entra em Falhas. A quebra por motivo aparece nos chips da
        // listagem abaixo, que é onde ela é acionável.
        { titulo: 'Falhas', valor: disparo?.falhas ?? 0, cor: 'var(--fin-dg)' },
        { titulo: 'Pendentes', valor: disparo?.pendentes ?? 0, cor: 'var(--fin-mt)' },
    ];

    const percentual = disparo && disparo.total > 0
        ? Math.round((disparo.processados / disparo.total) * 100)
        : 0;

    const rotuloStatus = {
        em_andamento: 'Disparo em andamento',
        concluido: 'Disparo concluído',
        abortado: 'Disparo interrompido',
    };

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div><h1 className="fin-pp__title">Disparo de Formulário</h1></div>
                    <div className="fin-pp__actions">
                        {/* Simular e Disparar criam lote MANUAL. Na aba dos automáticos eles
                            não aparecem — não é um botão desabilitado com explicação, é ação
                            que não pertence àquela categoria. Botão cinza convida a descobrir
                            por que está cinza; ausência não gera pergunta. */}
                        {aba === ORIGEM_MANUAL && (
                            <>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm"
                                    onClick={() => abrirConfirmacao({}, true)}
                                    disabled={enviando || emAndamento}
                                >
                                    <FaVial size={13} /> Simular
                                </button>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill"
                                    onClick={() => abrirConfirmacao({}, false)}
                                    disabled={enviando || emAndamento}
                                >
                                    <FaPaperPlane size={14} /> Disparar formulário
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm"
                            onClick={() => {
                                buscar();
                                if (aba === ABA_REDES) setRecargaRedes(n => n + 1);
                            }}
                            disabled={carregando}
                        >
                            <FaSyncAlt size={13} /> Atualizar
                        </button>
                    </div>
                </div>

                <div className="fin-pp__tabs mb-3">
                    {ABAS.map(item => (
                        <button
                            key={item.valor}
                            type="button"
                            className={'fin-pp__tab' + (aba === item.valor ? ' is-active' : '')}
                            onClick={() => setAba(item.valor)}
                        >
                            {item.rotulo}
                        </button>
                    ))}
                </div>

                {/* O card de acompanhamento é do lote MANUAL: contadores ao vivo, barra de
                    progresso e transporte (SSE ou polling) só fazem sentido para um envio que
                    alguém acabou de mandar e está olhando rodar. Na aba dos automáticos ele
                    mostraria o último lote da tela — que é de outra categoria — e diria ao
                    operador que o job fez algo que ele não fez. */}
                {aba === ORIGEM_AUTOMATICA && (
                    <div className="fin-pp__card" style={{ marginBottom: 16 }}>
                        <div className="fin-pp__cphead" style={{ padding: '14px 20px' }}>
                            <span className="fin-pp__cphead-ic"><FaRedo size={16} /></span>
                            <span className="fin-pp__cphead-title">Lembretes automáticos</span>
                        </div>
                        <div style={{ padding: '0 20px 18px' }}>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--fin-mt)', lineHeight: 1.6 }}>
                                {ABAS.find(item => item.valor === ORIGEM_AUTOMATICA)?.descricao}
                            </p>
                            {/* A regra dita por que um fornecedor aparece aqui uma vez e outro
                                duas. Sem ela, a lista parece arbitrária e a primeira pergunta
                                da área vira "por que fulano recebeu de novo?". */}
                            <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--fin-mt)', lineHeight: 1.6 }}>
                                Cada fornecedor é cobrado <strong>uma vez por dia</strong>, do 1º ao{' '}
                                <strong>7º</strong> dia após o convite, e só enquanto não responder. Quem
                                responde sai da fila na mesma hora; passado o 7º dia, o sistema para e a
                                cobrança passa a ser manual.
                            </p>

                            {/* Aciona a MESMA varredura que o agendador chama, pela rota
                                `fornecedor/lembretes/manual` — perfil em vez de token, rotina idêntica.
                                Existe porque o gatilho automático é configuração de infraestrutura: sem
                                este botão não há como exercitar a rotina antes de ela existir, nem
                                rodá-la à mão no dia em que ela falhar. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--sm"
                                    onClick={() => rodarVarredura(true)}
                                    disabled={varrendo || enviando || emAndamento}
                                >
                                    <FaVial size={12} /> Simular lembretes
                                </button>
                                <button
                                    type="button"
                                    className={'fin-pp__btn fin-pp__btn--sm' + (varreduraArmada ? ' fin-pp__btn--danger' : '')}
                                    onClick={() => (varreduraArmada ? rodarVarredura(false) : setVarreduraArmada(true))}
                                    disabled={varrendo || enviando || emAndamento}
                                >
                                    <FaPaperPlane size={12} />
                                    {varreduraArmada ? ' Confirmar — os e-mails saem agora' : ' Enviar lembretes'}
                                </button>
                                {varreduraArmada && (
                                    <button
                                        type="button"
                                        className="fin-pp__btn fin-pp__btn--sm"
                                        onClick={() => setVarreduraArmada(false)}
                                    >
                                        Cancelar
                                    </button>
                                )}
                                {varrendo && (
                                    <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>Enviando…</span>
                                )}
                            </div>

                            {varredura && (
                                <div
                                    className={'fin-pp__banner ' + (varredura.erro ? 'is-warn' : 'is-info')}
                                    style={{ marginTop: 12 }}
                                >
                                    <span>
                                        {varredura.erro || varredura.mensagem}
                                        {!varredura.erro && varredura.ocupados?.length > 0 && (
                                            <span style={{ display: 'block', marginTop: 4 }}>
                                                {`${varredura.ocupados.length} tenant(s) ficaram de fora: havia disparo em andamento. A próxima rodada os pega.`}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {aba === ABA_GERADOS && (
                    <div className="fin-pp__card" style={{ marginBottom: 16 }}>
                        <div className="fin-pp__cphead" style={{ padding: '14px 20px' }}>
                            <span className="fin-pp__cphead-ic"><FaLink size={16} /></span>
                            <span className="fin-pp__cphead-title">Links gerados</span>
                        </div>
                        <div style={{ padding: '0 20px 18px' }}>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--fin-mt)', lineHeight: 1.6 }}>
                                {ABAS.find(item => item.valor === ABA_GERADOS)?.descricao}
                            </p>
                        </div>
                    </div>
                )}

                {aba === ABA_REDES && (
                    <>
                        <div className="fin-pp__card" style={{ marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ padding: '14px 20px' }}>
                                <span className="fin-pp__cphead-ic"><FaSitemap size={16} /></span>
                                <span className="fin-pp__cphead-title">Envio por rede</span>
                            </div>
                            <div style={{ padding: '0 20px 18px' }}>
                                <p style={{ margin: 0, fontSize: 13, color: 'var(--fin-mt)', lineHeight: 1.6 }}>
                                    {ABAS.find(item => item.valor === ABA_REDES)?.descricao}
                                </p>
                            </div>
                        </div>

                        <DisparoRedes
                            token={token}
                            bloqueado={enviando || emAndamento}
                            recarga={recargaRedes}
                            onEnviar={(alvos, simulacao) => abrirConfirmacao({ alvos }, simulacao)}
                        />
                    </>
                )}

                {aba === ORIGEM_MANUAL && (
                <div className="fin-pp__card" style={{ marginBottom: 16 }}>
                    <div className="fin-pp__cphead" style={{ padding: '14px 20px' }}>
                        <span className="fin-pp__cphead-ic"><FaPaperPlane size={17} /></span>
                        <span className="fin-pp__cphead-title">
                            {disparo ? (rotuloStatus[disparo.status] || disparo.status) : 'Nenhum disparo registrado'}
                        </span>
                        {/* Não diz mais "nenhum e-mail enviado": com amostra, sai um. O que
                            continua verdade nos dois casos — e é o que importa — é que nenhum
                            FORNECEDOR recebeu. */}
                        {disparo?.simulacao && (
                            <span className="fin-pp__pill is-info">Simulação — fornecedores não receber{disparo?.status=="em_andamento"?"ão":"am"} o e-mail</span>
                        )}
                        {transporte && (
                            <span className="fin-pp__cphead-sub">
                                {transporte === 'sse'
                                    ? 'tempo real (SSE)'
                                    : `polling a cada ${INTERVALO_POLLING / 1000}s`}
                            </span>
                        )}
                    </div>

                    <div style={{ padding: 20 }}>
                        <div className="fin-pp__statusbar" style={{ flexWrap: 'wrap', gap: 12 }}>
                            {cards.map((card) => (
                                <div
                                    key={card.titulo}
                                    className="fin-pp__statuschip"
                                    style={{ flex: '1 1 140px', flexDirection: 'column', padding: '16px 12px', textAlign: 'center', gap: 4 }}
                                >
                                    <span className="fin-pp__statuschip-num" style={{ color: card.cor }}>
                                        {card.valor}
                                    </span>
                                    <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>{card.titulo}</span>
                                </div>
                            ))}
                        </div>

                        {disparo && disparo.total > 0 && (
                            <div style={{ marginTop: 20 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--fin-mt)', marginBottom: 6 }}>
                                    <span>{disparo.processados} de {disparo.total}</span>
                                    <span>{percentual}%</span>
                                </div>
                                <div style={{ height: 8, borderRadius: 999, background: 'var(--fin-bd)', overflow: 'hidden' }}>
                                    <div
                                        style={{
                                            height: '100%',
                                            width: `${percentual}%`,
                                            background: 'var(--fin-ac)',
                                            transition: 'width .3s ease',
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {disparo?.status === 'abortado' && (
                            <div className="fin-pp__banner is-warn" style={{ marginTop: 16 }}>
                                O disparo foi interrompido antes de terminar. Os {disparo.pendentes} fornecedores
                                pendentes não receberam o formulário.
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* Links gerados não dependem de disparo: um tenant que nunca disparou pode tê-los. */}
                {(disparo || aba === ABA_GERADOS) && !emAndamento && aba !== ABA_REDES && (
                    <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                        <div className="fin-pp__cphead" style={{ padding: '14px 20px' }}>
                            <span className="fin-pp__cphead-ic"><FaListUl size={16} /></span>
                            <span className="fin-pp__cphead-title">Destinatários</span>
                            <span className="fin-pp__cphead-sub">{totalEnvios} registros</span>
                        </div>

                        <div style={{ padding: '14px 20px 0' }}>
                            {/* Na aba de links gerados toda linha é `gerado`: as faixas de desfecho
                                do e-mail não se aplicam, nem o seletor de lote. */}
                            {aba !== ABA_GERADOS && (
                            <div className="fin-pp__statusbar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                                {FILTROS_ENVIO.map(f => (
                                    <button
                                        key={f.valor || 'todos'}
                                        type="button"
                                        className="fin-pp__statuschip"
                                        onClick={() => setFiltroStatus(f.valor)}
                                        style={{
                                            cursor: 'pointer',
                                            background: filtroStatus === f.valor ? 'var(--fin-acw)' : 'transparent',
                                            borderColor: filtroStatus === f.valor ? 'var(--fin-acb)' : 'var(--fin-bd)',
                                            color: filtroStatus === f.valor ? 'var(--fin-ac)' : 'var(--fin-mt)',
                                        }}
                                    >
                                        <span className="fin-pp__statuschip-num">
                                            {f.valor === 'nao_enviado'
                                                ? naoEnviados
                                                : f.valor ? (contadores[f.valor] ?? 0) : totalGeralContadores}
                                        </span>
                                        <span>{f.rotulo}</span>
                                    </button>
                                ))}
                            </div>
                            )}

                            <form
                                className="fin-pp__filters"
                                onSubmit={(e) => { e.preventDefault(); setBuscaAplicada(busca); }}
                            >
                                <div className="fin-pp__field fin-pp__field--grow">
                                    <span className="fin-pp__label">Buscar</span>
                                    <input
                                        className="fin-pp__input"
                                        style={{ paddingLeft: 14 }}
                                        placeholder="Nome, nome fantasia, CNPJ ou e-mail"
                                        value={busca}
                                        onChange={(e) => setBusca(e.target.value)}
                                    />
                                </div>
                                {aba !== ABA_GERADOS && (
                                <div className="fin-pp__field" style={{ minWidth: 260 }}>
                                    <span className="fin-pp__label">Disparo</span>
                                    {/* Marcar vários soma os DESTINATÁRIOS dos lotes, não os
                                        envios: quem recebeu em três aparece uma vez, com o
                                        último. É o mesmo agrupamento por fornecedor que a
                                        listagem sempre usou, então o rótulo do reenvio e o
                                        alvo do envio continuam contando a mesma coisa. */}
                                    <ReactSelect
                                        isMulti
                                        isClearable
                                        placeholder="Todos os disparos"
                                        options={opcoesDisparo}
                                        value={opcoesDisparo.filter(o => disparoFiltro.includes(o.value))}
                                        onChange={alterarDisparoFiltro}
                                    />
                                </div>
                                )}
                                <div className="fin-pp__field">
                                    <span className="fin-pp__label">{aba === ABA_GERADOS ? 'Gerado de' : 'Enviado de'}</span>
                                    <input
                                        type="date"
                                        className="fin-pp__input"
                                        style={{ paddingLeft: 14 }}
                                        value={desdeFiltro}
                                        onChange={(e) => setDesdeFiltro(e.target.value)}
                                    />
                                </div>
                                <div className="fin-pp__field">
                                    <span className="fin-pp__label">até</span>
                                    <input
                                        type="date"
                                        className="fin-pp__input"
                                        style={{ paddingLeft: 14 }}
                                        value={ateFiltro}
                                        onChange={(e) => setAteFiltro(e.target.value)}
                                    />
                                </div>
                                <div className="fin-pp__field">
                                    <span className="fin-pp__label">&nbsp;</span>
                                    <button type="submit" className="fin-pp__btn fin-pp__btn--primary">Buscar</button>
                                </div>
                            </form>

                            {/* Reenvio do recorte — o "Cobrar" do briefing. O freio contra
                                repetir o disparo não é mais este botão: é o dedupe, que tira
                                quem já recebeu e só devolve pelo checkbox do modal.

                                Fora da aba dos automáticos: reenviar cria lote MANUAL, e
                                oferecer isso na categoria que promete "não há o que disparar
                                aqui" desmente a própria aba. Quem quiser cobrar à mão
                                encontra os mesmos fornecedores na aba de disparos, pela
                                faixa de status. */}
                            {/* Na aba de links gerados o reenvio manda o e-mail a quem só recebeu o
                                link por fora — e o modal pergunta antes, pela caixa própria deles. */}
                            {(aba === ORIGEM_MANUAL || aba === ABA_GERADOS) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14, marginTop: 12 }}>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--sm"
                                    // `sem_email` já bloqueou o reenvio aqui, e não devia:
                                    // corrigir o cadastro e reenviar para essa mesma lista é
                                    // justamente o que se faz com ela. Quem não tiver e-mail
                                    // até lá volta a gravar `falha/sem_email` — o que é o
                                    // registro correto, e a legenda abaixo avisa antes.
                                    disabled={
                                        enviando
                                        || emAndamento
                                        // Não há mais o estado "nenhum filtro": o lote corrente
                                        // entra sozinho. O freio do reenvio deixou de ser este
                                        // botão e passou a ser o dedupe — fora da faixa
                                        // "Enviados", quem já recebeu sai do alvo e só volta
                                        // pela caixa do modal, com a contagem à vista.
                                    }
                                    onClick={() => abrirConfirmacao(
                                        // `busca` VAI JUNTO. O rótulo ao lado conta
                                        // `totalEnvios`, que já é filtrado por ela — sem
                                        // mandá-la, a tela dizia "3" e o disparo atingia
                                        // o recorte inteiro, num envio irreversível.
                                        // `recorte` diz ao servidor que o alvo é ESTA lista, e não
                                        // "todos os fornecedores ativos". Sem filtro nenhum os dois
                                        // chegariam idênticos ao backend, e o reenvio alcançaria
                                        // também quem nunca recebeu — mais gente do que a tela mostra.
                                        { recorte: true, statusEnvio: filtroStatus, disparo: disparoFiltro, desde: desdeFiltro, ate: ateFiltro, busca: buscaAplicada, origem: aba },
                                        false
                                    )}
                                >
                                    <FaRedo size={12} /> Reenviar este recorte
                                </button>
                                <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>
                                    {filtroStatus === 'sem_email'
                                        ? 'Estes fornecedores não tinham e-mail no envio — corrija o cadastro antes de reenviar, ou eles falharão de novo'
                                        // Agora a listagem é por fornecedor, então este
                                        // número já É de fornecedores. A confirmação ainda
                                        // pode mostrar menos: ela descarta quem foi
                                        // desativado desde o envio.
                                        : `${totalEnvios} fornecedor(es) neste recorte — a confirmação mostra quantos de fato receberão`}
                                </span>
                            </div>
                            )}
                        </div>

                        <div className="fin-pp__tblwrap">
                            <table className="fin-pp__tbl" style={{ minWidth: 900, tableLayout: 'auto' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: 60 }}></th>
                                        <th>Código</th>
                                        <th>Fornecedor</th>
                                        <th>CNPJ</th>
                                        <th>{aba === ABA_GERADOS ? 'Gerado por' : 'E-mail'}</th>
                                        <th>{aba === ABA_GERADOS ? 'Gerado em' : 'Último envio'}</th>
                                        <th>Situação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {carregandoEnvios ? (
                                        <tr><td colSpan={7} className="fin-pp__empty" style={{ padding: 0 }}><TabelaLoading /></td></tr>
                                    ) : envios.length === 0 ? (
                                        <tr><td colSpan={7} className="fin-pp__empty">Nenhum destinatário neste filtro</td></tr>
                                    ) : envios.map(envio => (
                                        /* A linha inteira abre o drawer de destinos. A lista responde
                                           "para quem foi e como terminou", e a pergunta seguinte é
                                           sempre sobre UM fornecedor — abrir o cadastro dele ou ver o
                                           que ele respondeu. Sem isto, o operador copia o nome, sai da
                                           tela e busca de novo em outro painel. */
                                        <tr
                                            key={envio.id}
                                            onClick={() => setLinhaEscolhida(envio)}
                                            style={{ cursor: 'pointer' }}
                                            title="Ver cadastro ou submissão deste fornecedor"
                                        >
                                            <td>
                                                {/* Atalho direto para o cadastro, como o lápis dos outros painéis;
                                                    o clique no resto da linha continua abrindo o drawer. */}
                                                <Link
                                                    to={'/cadastroFornecedor/' + envio.fornecedor}
                                                    className="fin-pp__rowbtn"
                                                    title="Abrir o cadastro do fornecedor"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <FaPen size={14} />
                                                </Link>
                                            </td>
                                            <td className="fin-pp__cell-muted">{envio.codigo ?? '—'}</td>
                                            <td className="fin-pp__cell-strong">
                                                {envio.nome ?? `(fornecedor ${envio.fornecedor})`}
                                                {envio.nomefantasia && envio.nomefantasia !== envio.nome && (
                                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', fontWeight: 400 }}>
                                                        {envio.nomefantasia}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="fin-pp__cell-muted">{envio.cnpj || '—'}</td>
                                            {aba === ABA_GERADOS ? (
                                                <td className={envio.usuario_nome ? '' : 'fin-pp__cell-muted'}>
                                                    {envio.usuario_nome || '—'}
                                                </td>
                                            ) : (
                                                <td className={envio.email_destinatario ? '' : 'fin-pp__cell-muted'}>
                                                    {envio.email_destinatario || '—'}
                                                </td>
                                            )}
                                            {/* A data é a do ÚLTIMO envio no recorte; a contagem
                                                embaixo é de VIDA INTEIRA do fornecedor, e é o que
                                                revela que houve cobrança — sem ela, quem foi
                                                cobrado três vezes fica idêntico a quem recebeu uma
                                                só. Daí o "ao todo": as duas informações têm
                                                recortes diferentes de propósito, e omitir isso
                                                faria a contagem parecer do período filtrado. */}
                                            <td className="fin-pp__cell-muted" style={{ whiteSpace: 'nowrap' }}>
                                                {formatarDataHora(envio.created_at)}
                                                {envio.envios > 1 && (
                                                    <span style={{ display: 'block', fontSize: 12 }}>
                                                        {`${envio.envios} envios ao todo`}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={'fin-pp__pill ' + (PILL_STATUS[faixaDoEnvio(envio)] || 'is-muted')}>
                                                    {ROTULO_STATUS_ENVIO[faixaDoEnvio(envio)] || envio.status}
                                                </span>
                                                {faixaDoEnvio(envio) === 'falha' && envio.erro && (
                                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', marginTop: 4 }}>
                                                        {envio.erro}
                                                    </span>
                                                )}
                                                {envio.status === 'gerado' && envio.token_expira_em && (
                                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', marginTop: 4 }}>
                                                        {`vale até ${formatarDataHora(envio.token_expira_em)}`}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="fin-pp__pagination">
                            <div className="fin-pp__pagination-info">{totalEnvios} fornecedores</div>
                            <div className="fin-pp__pagination-ctrls">
                                <select
                                    className="fin-pp__select"
                                    style={{ height: 34 }}
                                    value={porPagina}
                                    onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }}
                                >
                                    {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--sm"
                                    disabled={pagina <= 1}
                                    onClick={() => setPagina(p => p - 1)}
                                >
                                    Anterior
                                </button>
                                <span className="fin-pp__pagination-info">{pagina} / {totalPaginas}</span>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--sm"
                                    disabled={pagina >= totalPaginas}
                                    onClick={() => setPagina(p => p + 1)}
                                >
                                    Próxima
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {confirmacao && (
                <div
                    className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget) setConfirmacao(null); }}
                >
                    {/* Idem revisão, e aqui pesa mais: com os avisos empilhados (recorte
                        sem status, alvo grande, já enviados, amostra) o corpo passa da
                        altura da tela, e o que sai de vista é o rodapé — onde fica o
                        botão de disparo real, que não tem desfazer. */}
                    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className={'fin-pp__modal-head-ic' + (confirmacao.simulacao ? '' : ' is-danger')}>
                                    {confirmacao.simulacao ? <FaVial size={17} /> : <FaExclamationTriangle size={17} />}
                                </span>
                                <h3 className="fin-pp__modal-title">
                                    {confirmacao.simulacao ? 'Simular disparo' : 'Confirmar disparo real'}
                                    <span className="fin-pp__modal-sub">
                                        {confirmacao.simulacao
                                            ? (amostraDigitada.length > 0
                                                ? `Percorre o fluxo sem enviar aos fornecedores; sai uma amostra para ${amostraDigitada.length} endereço(s)`
                                                : 'Percorre o fluxo e registra tudo, sem enviar e-mail')
                                            : 'Esta ação envia e-mail de verdade e não pode ser desfeita'}
                                    </span>
                                </h3>
                                <button
                                    type="button"
                                    className="fin-pp__modal-close"
                                    onClick={() => setConfirmacao(null)}
                                >
                                    <FaTimes size={16} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {confirmacao.carregando ? (
                                    <div className="fin-pp__empty">Calculando a seleção…</div>
                                ) : !confirmacao.previa ? (
                                    <div className="fin-pp__banner is-warn">
                                        Não foi possível calcular a seleção. Tente novamente.
                                    </div>
                                ) : (
                                    <>
                                        {/* A regra de destinatário não é dedutível da tela: a seleção
                                            é por fornecedor, mas o envio é por CONTATO, e só dois
                                            papéis recebem. Sem isto aqui, o operador seleciona 200
                                            fornecedores, confirma, e descobre depois — pelos
                                            `sem e-mail` — que boa parte não recebeu.
                                            Ver FornecedorDisparoController::destinatariosDe(). */}
                                        <div className="fin-pp__banner is-info" style={{ marginBottom: 14 }}>
                                            {/* Um filho só: `.fin-pp__banner` é flex, então texto solto
                                                misturado com <strong> vira um item por fragmento e cada
                                                pedaço ganha a própria coluna. */}
                                            <span>
                                                O formulário vai <strong>somente</strong> para os contatos de área
                                                <strong> Fiscal</strong> e <strong>Financeiro</strong> do fornecedor —
                                                um recebe e o outro fica em cópia. Contato comercial, sem categoria
                                                ou responsável por CBS/IBS não recebe. Fornecedor que não tiver
                                                nenhum dos dois é registrado como <strong>sem e-mail</strong> e não
                                                recebe nada.
                                            </span>
                                        </div>

                                        <div className="fin-pp__statusbar" style={{ gap: 12, marginBottom: 14 }}>
                                            <div
                                                className="fin-pp__statuschip"
                                                style={{ flex: '1 1 140px', flexDirection: 'column', padding: '16px 12px', textAlign: 'center', gap: 4 }}
                                            >
                                                <span
                                                    className="fin-pp__statuschip-num"
                                                    style={{ color: confirmacao.previa.alvos > 0 ? 'var(--fin-tx)' : 'var(--fin-mt)' }}
                                                >
                                                    {confirmacao.previa.alvos}
                                                </span>
                                                <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>
                                                    {confirmacao.simulacao ? 'Serão processados' : 'Receberão o formulário'}
                                                </span>
                                            </div>
                                            {confirmacao.previa.jaEnviados > 0 && (
                                                <div
                                                    className="fin-pp__statuschip"
                                                    style={{ flex: '1 1 140px', flexDirection: 'column', padding: '16px 12px', textAlign: 'center', gap: 4 }}
                                                >
                                                    <span className="fin-pp__statuschip-num" style={{ color: 'var(--fin-wn)' }}>
                                                        {confirmacao.previa.jaEnviados}
                                                    </span>
                                                    <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>Já receberam</span>
                                                </div>
                                            )}
                                            {confirmacao.previa.jaGerados > 0 && (
                                                <div
                                                    className="fin-pp__statuschip"
                                                    style={{ flex: '1 1 140px', flexDirection: 'column', padding: '16px 12px', textAlign: 'center', gap: 4 }}
                                                >
                                                    <span className="fin-pp__statuschip-num" style={{ color: 'var(--fin-wn)' }}>
                                                        {confirmacao.previa.jaGerados}
                                                    </span>
                                                    <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>Com link gerado</span>
                                                </div>
                                            )}
                                            {confirmacao.previa.jaPreencheram > 0 && (
                                                <div
                                                    className="fin-pp__statuschip"
                                                    style={{ flex: '1 1 140px', flexDirection: 'column', padding: '16px 12px', textAlign: 'center', gap: 4 }}
                                                >
                                                    <span className="fin-pp__statuschip-num" style={{ color: 'var(--fin-ok)' }}>
                                                        {confirmacao.previa.jaPreencheram}
                                                    </span>
                                                    <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>Já preencheram</span>
                                                </div>
                                            )}
                                            {confirmacao.previa.naoPreencheram > 0 && (
                                                <div
                                                    className="fin-pp__statuschip"
                                                    style={{ flex: '1 1 140px', flexDirection: 'column', padding: '16px 12px', textAlign: 'center', gap: 4 }}
                                                >
                                                    <span className="fin-pp__statuschip-num" style={{ color: 'var(--fin-wn)' }}>
                                                        {confirmacao.previa.naoPreencheram}
                                                    </span>
                                                    <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>Ainda não preencheram</span>
                                                </div>
                                            )}
                                        </div>

                                        {confirmacao.previa.jaEnviados > 0 && (
                                            <div className="fin-pp__banner is-warn">
                                                <span>
                                                    {confirmacao.previa.jaEnviados} fornecedor(es) foram excluídos por já
                                                    terem recebido o formulário. Incluí-los envia o e-mail uma segunda vez.
                                                </span>
                                            </div>
                                        )}

                                        {/* Reenvio: TODO o recorte já recebeu, por definição.
                                            O banner acima nunca aparece aqui, porque o dedupe é
                                            deliberadamente pulado no reenvio (o recorte É gente
                                            que já recebeu) — e o efeito era o modal informar
                                            honestamente QUANTOS, e calar sobre ser repetição.
                                            Escolher um lote no seletor e confirmar mandava o
                                            e-mail de novo para o lote inteiro sem nada na tela
                                            dizendo isso. */}
                                        {confirmacao.previa.revisao && confirmacao.previa.alvos > 0 && (
                                            <div className="fin-pp__banner is-warn">
                                                <span>
                                                    <strong>Pedido de atualização de cadastro.</strong> Os{' '}
                                                    {confirmacao.previa.alvos} já entregaram o formulário e vão
                                                    recebê-lo outra vez, para revisar o que declararam.
                                                    {confirmacao.previa.naoPreencheram > 0
                                                        ? ` Quem ainda não entregou (${confirmacao.previa.naoPreencheram}) ficou de fora — para esses, use Cobrar.`
                                                        : ''}
                                                </span>
                                            </div>
                                        )}

                                        {/* O aviso de reenvio saiu daqui. Ele afirmava que TODOS os
                                            alvos já tinham recebido, inferindo isso de `origem ===
                                            'reenvio'` — que só diz de onde veio o recorte, não o que
                                            sobrou nele. Com o dedupe rodando fora da faixa "Enviados",
                                            os alvos restantes são justamente quem NÃO recebeu, e o
                                            modal afirmava o contrário na última tela antes de um envio
                                            que não se desfaz. Quem sabe a verdade são os contadores
                                            logo acima, e a caixa abaixo diz o que fazer com ela. */}
                                        {/* Visível SEMPRE que significa alguma coisa, e não só quando
                                            o dedupe já mordeu alguém: aparecendo junto com a contagem
                                            que ela explica, o operador vê a opção antes de precisar
                                            dela, e ela não some ao ser desmarcada.
                                            Fora na cobrança e na revisão, onde alcançar quem já
                                            recebeu é intrínseco à ação e o backend descarta a flag —
                                            desmarcar ali recarregava a prévia e devolvia o mesmo
                                            número, o que lê como tela travada. `previa.revisao` e não
                                            `confirmacao.revisao`: quem decide se a revisão vale é o
                                            servidor, que a descarta quando vem junto da cobrança. */}
                                        {!confirmacao.previa.revisao && !confirmacao.cobranca ? (
                                            <div className="fin-pp__checks">
                                                <label className="fin-pp__check-row">
                                                    <input
                                                        type="checkbox"
                                                        className="fin-pp__check"
                                                        checked={confirmacao.incluirJaEnviados}
                                                        onChange={(e) => alternarJaEnviados(e.target.checked)}
                                                    />
                                                    <span className="fin-pp__check-label">
                                                        Reenviar também para quem já recebeu
                                                        {confirmacao.previa.jaEnviados > 0
                                                            ? ` (${confirmacao.previa.jaEnviados} fora agora)`
                                                            : ''}
                                                    </span>
                                                </label>
                                            </div>
                                        ) : null}

                                        {/* Pergunta própria, fora da caixa de "já recebeu": o link foi
                                            entregue à mão, e o e-mail seria um segundo contato por
                                            outro canal. Aparece na cobrança também — lá o "já recebeu"
                                            vem ligado por dentro, mas quem tem link gerado segue de
                                            fora até alguém marcar aqui. */}
                                        {!confirmacao.previa.revisao && (confirmacao.previa.jaGerados > 0 || confirmacao.incluirGerados) ? (
                                            <>
                                                {confirmacao.previa.jaGerados > 0 && (
                                                    <div className="fin-pp__banner is-warn">
                                                        <span>
                                                            {confirmacao.previa.jaGerados} fornecedor(es) foram excluídos por terem link
                                                            gerado no cadastro, entregue por fora do sistema. Incluí-los manda também
                                                            o e-mail.
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="fin-pp__checks">
                                                    <label className="fin-pp__check-row">
                                                        <input
                                                            type="checkbox"
                                                            className="fin-pp__check"
                                                            checked={confirmacao.incluirGerados}
                                                            onChange={(e) => alternarGerados(e.target.checked)}
                                                        />
                                                        <span className="fin-pp__check-label">
                                                            Reenviar também para quem tem link gerado
                                                            {confirmacao.previa.jaGerados > 0
                                                                ? ` (${confirmacao.previa.jaGerados} fora agora)`
                                                                : ''}
                                                        </span>
                                                    </label>
                                                </div>
                                            </>
                                        ) : null}

                                        {confirmacao.previa.jaPreencheram > 0 && !confirmacao.revisao && (
                                            <div className="fin-pp__banner is-info">
                                                <span>
                                                    {confirmacao.previa.jaPreencheram} fornecedor(es) foram excluídos por já
                                                    terem preenchido o formulário
                                                    {confirmacao.cobranca
                                                        ? ' — cobrança não os alcança, e é isso que ela significa.'
                                                        : '. Só inclua se a intenção for pedir o cadastro de novo — uma revisão periódica, por exemplo.'}
                                                </span>
                                            </div>
                                        )}

                                        {/* Não aparece na cobrança nem na revisão, pelos dois
                                            motivos opostos: cobrar é alcançar quem NÃO preencheu, e
                                            a revisão já mira exatamente quem preencheu. Nos dois o
                                            backend ignora a flag. */}
                                        {!confirmacao.cobranca && !confirmacao.revisao && (confirmacao.previa.jaPreencheram > 0 || confirmacao.incluirJaPreencheram) ? (
                                            <div className="fin-pp__checks">
                                                <label className="fin-pp__check-row">
                                                    <input
                                                        type="checkbox"
                                                        className="fin-pp__check"
                                                        checked={confirmacao.incluirJaPreencheram}
                                                        onChange={(e) => alternarJaPreencheram(e.target.checked)}
                                                    />
                                                    <span className="fin-pp__check-label">
                                                        Enviar também para quem já preencheu
                                                    </span>
                                                </label>
                                            </div>
                                        ) : null}

                                        {confirmacao.simulacao && confirmacao.previa.alvos > 0 && (
                                            <div style={{ marginTop: 14 }}>
                                                <FinField label="Digite o e-mail para simulação">
                                                    <FinInput
                                                        type="text"
                                                        placeholder="otavio@email.com, joao@email.com"
                                                        value={confirmacao.emailsAmostra}
                                                        onChange={(e) => setConfirmacao(c => c && ({ ...c, emailsAmostra: e.target.value }))}
                                                    />
                                                    <span style={{ fontSize: 12, color: 'var(--fin-mt)', marginTop: 6, display: 'block' }}>
                                                        Opcional, até 5 separados por vírgula. Sai uma única mensagem para
                                                        estes endereços, com link real de um fornecedor da seleção. Nenhum
                                                        fornecedor recebe nada.
                                                    </span>
                                                </FinField>
                                                {erroAmostra && (
                                                    <div className="fin-pp__banner is-warn" style={{ marginTop: 8 }}>
                                                        <span>{erroAmostra}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {confirmacao.previa.alvos === 0 && (
                                            <div className="fin-pp__banner is-info" style={{ marginTop: 12 }}>
                                                {confirmacao.previa.revisao
                                                    ? 'Nenhum fornecedor da seleção preencheu o formulário — não há cadastro para revisar.'
                                                    : confirmacao.previa.jaGerados > 0 && confirmacao.previa.jaEnviados === 0 && confirmacao.previa.jaPreencheram === 0
                                                        ? 'Todos os fornecedores da seleção têm link gerado. Marque "reenviar também para quem tem link gerado" para incluí-los.'
                                                    : confirmacao.previa.jaPreencheram > 0 && confirmacao.previa.jaEnviados === 0
                                                        ? (confirmacao.cobranca
                                                            ? 'Todos os fornecedores da seleção já preencheram o formulário — não há o que cobrar.'
                                                            : 'Todos os fornecedores da seleção já preencheram o formulário. Marque "enviar também para quem já preencheu" para incluí-los.')
                                                        : 'Nenhum fornecedor na seleção. Ajuste o filtro ou marque o reenvio.'}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="fin-pp__modal-foot">
                                <button
                                    type="button"
                                    className="fin-pp__btn"
                                    onClick={() => setConfirmacao(null)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className={'fin-pp__btn ' + (confirmacao.simulacao ? 'fin-pp__btn--primary' : 'fin-pp__btn--danger')}
                                    disabled={
                                        confirmacao.carregando
                                        || !confirmacao.previa
                                        || confirmacao.previa.alvos === 0
                                        || !!erroAmostra
                                        // A confirmação aberta pelo navState (seleção vinda do
                                        // Painel de Fornecedores) não passava pela trava dos
                                        // botões do cabeçalho: com um lote rodando, o operador
                                        // confirmava, levava 409, e a seleção já tinha sido
                                        // consumida pelo history.replace — remarcava os 30 na mão.
                                        || enviando
                                        || emAndamento
                                    }
                                    onClick={confirmarDisparo}
                                >
                                    {confirmacao.simulacao
                                        ? (amostraDigitada.length > 0 ? 'Simular e enviar amostra' : 'Simular')
                                        : `Enviar para ${confirmacao.previa?.alvos ?? 0}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Drawer de destinos da linha — abre pelo clique em qualquer linha da
                lista de envios. A skill de UI reserva o drawer para muitas ações e
                indicaria action-sheet para duas; ficou drawer porque é o que a área
                pediu, e porque o cabeçalho precisa repetir de quem é a linha (nome,
                CNPJ, destinatário, status) antes de o operador sair da tela. */}
            {linhaEscolhida && (
                <div className="fin-pp fin-pp__drawer-backdrop" onClick={() => setLinhaEscolhida(null)}>
                    <div className="fin-pp__drawer" onClick={(e) => e.stopPropagation()}>
                        <div className="fin-pp__drawer-head">
                            <span className="fin-pp__drawer-head-ic"><FaBuilding size={16} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h3 className="fin-pp__drawer-title">Fornecedor</h3>
                                <p className="fin-pp__drawer-sub">
                                    {linhaEscolhida.nome ?? `(fornecedor ${linhaEscolhida.fornecedor})`}
                                </p>
                                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--fin-mt)', lineHeight: 1.5 }}>
                                    {linhaEscolhida.cnpj || 'sem CNPJ'}
                                    {linhaEscolhida.email_destinatario && (
                                        <span style={{ display: 'block' }}>{linhaEscolhida.email_destinatario}</span>
                                    )}
                                </p>
                                <span
                                    className={'fin-pp__pill ' + (PILL_STATUS[faixaDoEnvio(linhaEscolhida)] || 'is-muted')}
                                    style={{ marginTop: 8 }}
                                >
                                    {ROTULO_STATUS_ENVIO[faixaDoEnvio(linhaEscolhida)] || linhaEscolhida.status}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="fin-pp__drawer-close"
                                onClick={() => setLinhaEscolhida(null)}
                            >
                                <FaTimes size={16} />
                            </button>
                        </div>
                        <div className="fin-pp__drawer-body">
                            <div className="fin-pp__drawer-section">Ir para</div>

                            <button
                                type="button"
                                className="fin-pp__drawer-item"
                                onClick={() => history.push('/cadastroFornecedor/' + linhaEscolhida.fornecedor)}
                            >
                                <span className="fin-pp__drawer-item-ic"><FaIdCard size={15} /></span>
                                <span className="fin-pp__drawer-item-label">Cadastro do fornecedor</span>
                            </button>

                            {/* A fila de revisão é por status, não por fornecedor: a rota
                                não recebe id. O nome vai no `state` para a tela abrir já
                                filtrada nele, em vez de o operador procurar na lista. */}
                            <button
                                type="button"
                                className="fin-pp__drawer-item"
                                onClick={() => history.push({
                                    pathname: '/fornecedorSubmissao',
                                    state: { fornecedor: linhaEscolhida.fornecedor, nome: linhaEscolhida.nome },
                                })}
                            >
                                <span className="fin-pp__drawer-item-ic"><FaClipboardCheck size={15} /></span>
                                <span className="fin-pp__drawer-item-label">Submissão / revisão</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ErpTemplate>
    );
}
